import { fetchPublicListings } from '../services/listings';
import { COUNTRIES, REGIONS } from '../data/geo';
import { supabase } from '../lib/supabaseClient';

const FALLBACK_IMG = "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=600&h=800&q=80";

// Catalogue-decides normalisation (see feedback_catalogue_geography_rule). A stray UK-family
// slug must fold into England, not spawn a phantom country. Scotland/Wales stay distinct.
const COUNTRY_SLUG_ALIASES: Record<string, string> = {
  'united-kingdom': 'england', 'uk': 'england', 'great-britain': 'england', 'britain': 'england',
};
const normCountrySlug = (s: string | null | undefined): string => {
  const k = String(s || '').toLowerCase().trim();
  return COUNTRY_SLUG_ALIASES[k] || k;
};
// Display defense: a country not in the geo list must never render as a raw lowercase
// slug ("costa-rica"). Title-case the slug as a readable fallback ("Costa Rica").
const titleCaseSlug = (s: string): string =>
  s.split('-').map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');

// Fallback destination summaries for countries with listings but no curated geo entry, so
// every destination reads consistently. Concise and evocative in the geo ai.summary voice,
// and accurate, true descriptive character only, no fabricated venues or figures.
const COUNTRY_SUMMARIES: Record<string, string> = {
  austria: "Imperial palaces, baroque halls, and Alpine-lake estates. Vienna and Salzburg carry an old-world, formal grandeur.",
  bahamas: "Private islands, powder-white sand, and turquoise shallows. Nassau and the Exumas anchor barefoot-luxury celebrations.",
  'british-virgin-islands': "Secluded cays, yacht-charter ceremonies, and villa estates above the Caribbean. Ultimate privacy for small gatherings.",
  cambodia: "Temple backdrops, riverside colonial hotels, and jungle sanctuaries. Siem Reap pairs Angkor grandeur with intimate ceremonies.",
  'costa-rica': "Rainforest canopies, Pacific clifftops, and eco-luxury lodges. Guanacaste leads for beach-and-jungle destination weddings.",
  cyprus: "Mediterranean coastlines, stone-built villages, and clifftop resorts. Paphos and Limassol offer sun-sure island celebrations.",
  'czech-republic': "Fairytale castles, baroque Prague palaces, and Moravian vineyard estates. Gothic grandeur at the heart of Europe.",
  denmark: "Manor houses, coastal estates, and design-led city venues. Copenhagen brings an understated Scandinavian elegance.",
  malaysia: "Rainforest resorts, island retreats, and colonial-era grand hotels. Langkawi and Penang blend tropical and heritage settings.",
  mexico: "Hacienda estates, Riviera Maya beaches, and colonial courtyards. Los Cabos and Tulum lead for destination glamour.",
  montenegro: "Adriatic fjords, stone-walled Kotor, and clifftop island resorts. A rising Mediterranean alternative to its neighbours.",
  qatar: "Desert resorts, waterfront palaces, and grand ballrooms. Doha delivers opulent, large-scale celebrations.",
  'saudi-arabia': "Desert palace resorts, Red Sea coastlines, and heritage estates. An emerging ultra-luxury destination.",
  switzerland: "Alpine lakesides, belle-epoque grand hotels, and private chalets. Lake Geneva and the Bernese Oberland frame intimate mountain weddings.",
  thailand: "Beachfront villas, island resorts, and tropical estates. Phuket and Koh Samui lead for barefoot-luxury weddings.",
};

export interface DestinationRegion {
  slug: string;
  name: string;
  venueCount: number;
  vendorCount: number;
}

export interface DestinationCountry {
  slug: string;
  name: string;
  image: string;
  aiSummary: string;
  venueCount: number;
  vendorCount: number;
  regions: DestinationRegion[];
}

export async function getDestinationHierarchy(): Promise<{ destinations: DestinationCountry[]; listings: ReturnType<typeof fetchPublicListings> extends Promise<infer T> ? T : never[] }> {
  try {
    // Fetch all published listings, LEAN columns only. This page needs nothing more than
    // the grouping keys (country/region/type/category), the heat-map coordinates (lat/lng,
    // name, slug, region) and a thumbnail. Pulling select('*') here dragged ~10.9 MB of heavy
    // JSONB per load and was the trigger for a DB overload (522). Lean = ~90 KB.
    const listings = await fetchPublicListings({
      columns: 'id,name,slug,country_slug,region_slug,region,city,lat,lng,is_featured,category_slug,listing_type,card_image,hero_image',
    });

    if (!listings || listings.length === 0) {
      return { destinations: [], listings: [] };
    }

    // Group by country → region
    const grouped: Record<
      string,
      {
        regions: Record<string, { venueCount: number; vendorCount: number }>;
        venueCount: number;
        vendorCount: number;
      }
    > = {};

    listings.forEach((listing) => {
      const countrySlug = normCountrySlug(listing.countrySlug);
      const regionSlug = listing.regionSlug || null;
      const isVenue = listing.type === "venue" || listing.category === "wedding-venues";

      if (!grouped[countrySlug]) {
        grouped[countrySlug] = { regions: {}, venueCount: 0, vendorCount: 0 };
      }

      // Count by type
      if (isVenue) {
        grouped[countrySlug].venueCount++;
      } else {
        grouped[countrySlug].vendorCount++;
      }

      // Group regions
      if (regionSlug) {
        if (!grouped[countrySlug].regions[regionSlug]) {
          grouped[countrySlug].regions[regionSlug] = { venueCount: 0, vendorCount: 0 };
        }
        if (isVenue) {
          grouped[countrySlug].regions[regionSlug].venueCount++;
        } else {
          grouped[countrySlug].regions[regionSlug].vendorCount++;
        }
      }
    });

    // Country images WITHOUT the N+1. Previously this fired one homepage_destinations query
    // PLUS a select('*') fallback PER country (~44 queries, blocking the whole page). Now:
    //   1. ONE batched homepage_destinations query → featured images.
    //   2. The fallback thumbnail comes from the listings ALREADY in memory (cardImage /
    //      heroImage, the lean fetch pulled them), so no per-country round-trip at all.
    const featuredMap = new Map<string, string>();
    try {
      const { data: featured } = await supabase
        .from('homepage_destinations')
        .select('country_slug, homepage_image')
        .eq('type', 'country');
      for (const f of (featured ?? []) as Array<{ country_slug: string; homepage_image: string | null }>) {
        if (f.homepage_image) featuredMap.set(normCountrySlug(f.country_slug), f.homepage_image);
      }
    } catch (error) {
      console.error('Error fetching homepage_destinations images:', error);
    }

    // First usable in-memory thumbnail per country (listings are ordered is_featured-first,
    // so the strongest image leads). cardImage is the purpose-built thumbnail; heroImage backs it.
    const firstImgByCountry = new Map<string, string>();
    for (const l of listings as Array<Record<string, unknown>>) {
      const cs = normCountrySlug(l.countrySlug as string | undefined);
      if (!cs || firstImgByCountry.has(cs)) continue;
      const img = (l.cardImage as string) || (l.heroImage as string);
      if (img) firstImgByCountry.set(cs, img);
    }

    const destinations = Object.entries(grouped).map(([countrySlug, data]) => {
      const country = COUNTRIES.find((c) => c.slug === countrySlug);
      const name = country?.name || titleCaseSlug(countrySlug);
      const image = featuredMap.get(countrySlug) || firstImgByCountry.get(countrySlug) || FALLBACK_IMG;

      const regions: DestinationRegion[] = Object.entries(data.regions)
        .map(([regionSlug, counts]) => {
          const region = REGIONS.find(
            (r) => r.slug === regionSlug && r.countrySlug === countrySlug
          );
          return {
            slug: regionSlug,
            name: region?.name || regionSlug,
            venueCount: counts.venueCount,
            vendorCount: counts.vendorCount,
          };
        })
        .sort((a, b) => (b.venueCount + b.vendorCount) - (a.venueCount + a.vendorCount));

      return {
        slug: countrySlug,
        name,
        image,
        aiSummary: country?.ai?.summary || COUNTRY_SUMMARIES[countrySlug] || `Discover luxury wedding venues and vendors across ${name}.`,
        venueCount: data.venueCount,
        vendorCount: data.vendorCount,
        regions,
      };
    });

    return {
      destinations: destinations.sort(
        (a, b) => (b.venueCount + b.vendorCount) - (a.venueCount + a.vendorCount)
      ),
      listings,
    };
  } catch (error) {
    console.error("Error getting destination hierarchy:", error);
    return { destinations: [], listings: [] };
  }
}
